import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Paper,
    Slide,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const BulkDeleteModal = ({ open, handleClose, handleConfirm, requests, isLoading }) => {
    if (!requests || requests.length === 0) return null;

    const totalCredits = requests.reduce((sum, req) => sum + (req.credits || 0), 0);
    const totalAmount = requests.reduce((sum, req) => sum + (req.amount || 0), 0);

    // Group by status for summary
    const statusCounts = requests.reduce((acc, req) => {
        acc[req.status] = (acc[req.status] || 0) + 1;
        return acc;
    }, {});

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return '#f59e0b';
            case 'Approved': return '#22c55e';
            case 'Rejected': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const StatCard = ({ icon, value, label, color }) => (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                flex: 1,
                minWidth: 100,
                background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
                border: '1px solid',
                borderColor: `${color}30`,
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
            }}
        >
            <Box sx={{ color: color }}>{icon}</Box>
            <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: color, lineHeight: 1.2 }}>
                    {value}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
        </Paper>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Gradient Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #487fff 0%, #6366f1 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            Bulk Delete Requests
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            disabled={isLoading}
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Box sx={{ px: 2.5, pb: 2.5, pt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                bgcolor: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                            }}
                        >
                            <Icon icon="mdi:delete-sweep" width={32} style={{ color: '#487fff' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                                Delete {requests.length} Request{requests.length > 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                This action cannot be undone
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                {/* Stats Section */}
                <Box sx={{ px: 2.5, py: 2, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <StatCard
                            icon={<Icon icon="mdi:file-document-multiple" width={24} />}
                            value={requests.length}
                            label="Total Requests"
                            color="#ef4444"
                        />
                        <StatCard
                            icon={<Icon icon="mdi:message-badge" width={24} />}
                            value={totalCredits.toLocaleString()}
                            label="Total Credits"
                            color="#3b82f6"
                        />
                        <StatCard
                            icon={<Icon icon="mdi:currency-inr" width={24} />}
                            value={`₹${totalAmount.toLocaleString()}`}
                            label="Total Amount"
                            color="#8b5cf6"
                        />
                    </Box>
                </Box>

                <Box sx={{ p: 2.5 }}>
                    {/* Status Breakdown */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'error.light',
                            backgroundColor: '#fef2f2',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Icon icon="mdi:alert-circle" width={18} style={{ color: '#ef4444' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                Status Breakdown
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {Object.entries(statusCounts).map(([status, count]) => (
                                <Chip
                                    key={status}
                                    label={`${status}: ${count}`}
                                    size="small"
                                    sx={{
                                        bgcolor: getStatusColor(status),
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            ))}
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Icon icon="solar:document-text-bold" width={18} style={{ color: '#3b82f6' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Requests to be Deleted
                            </Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 220 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Society</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Credits</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {requests.map((req) => (
                                        <TableRow key={req.id} hover>
                                            <TableCell sx={{ fontSize: '0.813rem' }}>{req.societyName}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={req.status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: getStatusColor(req.status),
                                                        color: 'white',
                                                        fontWeight: 500,
                                                        fontSize: '0.688rem',
                                                        height: 22,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem', fontWeight: 500 }}>
                                                {req.credits?.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.813rem' }}>
                                                ₹{req.amount?.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, pt: 0, bgcolor: '#f8fafc', gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isLoading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="error"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:delete-sweep" />}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    {isLoading ? 'Deleting...' : `Delete All (${requests.length})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkDeleteModal;
